import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';
import { getUserData } from '../../../utils/FunctionHelpers.js';
import { handleChangeRowsPerPage, getRowsPerPage } from '../../../utils/DataTables';
import DialogDeleteCustom from '../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { deleteMission } from '../../../services/MissionService';
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from '../../../utils/gridStyles';
import { DataGrid } from '../../../components/SortableDataGrid/SortableDataGrid';

moment.locale('fr');

const tableName = 'missionsColumnVisibility';
const CustomToolbar = makeGridToolbar('Liste des missions');

const statusConfig = {
  'en cours': { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  'terminée': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'annulée': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

const MissionsTable = ({ missionData, editMission, snackbarShowMessage, onReload }) => {
  const [loadToDelete, setLoadToDelete] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [missionToDelete, setMissionToDelete] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: getRowsPerPage(tableName) ?? 50 });

  const handleCloseDialog = () => setOpenDialogDelete(false);

  const handleDeleteMission = async (uuid) => {
    try {
      setLoadToDelete(true);
      snackbarShowMessage('Patientez svp...', 'warning', null);
      await deleteMission(uuid);
      snackbarShowMessage('Mission supprimée avec succès', 'success', 4000);
      setOpenDialogDelete(false);
      if (onReload) onReload();
    } catch (err) {
      snackbarShowMessage(err?.message || 'Erreur à la suppression de la mission', 'error', 6000);
    } finally {
      setLoadToDelete(false);
    }
  };

  const handlePaginationModelChange = (newModel) => {
    handleChangeRowsPerPage(tableName, newModel.pageSize);
    setPaginationModel(newModel);
  };

  const rows = (missionData?.data || []).map((item) => ({ ...item }));

  const columns = [
    {
      field: 'numMission', headerName: 'N°', width: 80,
      renderCell: (p) => (
        <RouterLink to={`/societe/${p.row.societeId}/missions/${p.row.uuid}/view`}
          style={{ textDecoration: 'none', color: '#03407b', fontWeight: 600, fontSize: '1rem' }}>
          {p.value}
        </RouterLink>
      ),
    },
    {
      field: 'client', headerName: 'CLIENT', width: 150,
      renderCell: (p) => (
        <RouterLink to={`/societe/${p.row.societeId}/view`}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontWeight: 500, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </RouterLink>
      ),
    },
    {
      field: 'nom_mission', headerName: 'NOM MISSION', width: 270,
      renderCell: (p) => (
        <RouterLink to={`/societe/${p.row.societeId}/missions/${p.row.uuid}/view`}
          style={{ textDecoration: 'none', color: '#0f172a', fontWeight: 600, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
          {p.value}
        </RouterLink>
      ),
    },
    {
      field: 'nb_postes', headerName: 'POSTES', width: 90, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <RouterLink to={{ pathname: `/candidats/missions/${p.row.uuid}/list`, search: `?quantity=${p.value}&isSelected=true` }}
          style={{ textDecoration: 'none' }}>
          <Chip label={p.value ?? 0} size="small" sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }} />
        </RouterLink>
      ),
    },
    {
      field: 'cv-non-selected', headerName: 'CV PRESSENTIS', width: 130, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <RouterLink to={{ pathname: `/candidats/missions/${p.row.uuid}/list`, search: `?quantity=${p.value}&isSelected=false` }}
          style={{ textDecoration: 'none' }}>
          <Chip label={p.value ?? 0} size="small" sx={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }} />
        </RouterLink>
      ),
    },
    {
      field: 'CV', headerName: 'CV EN COURS', width: 120, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <RouterLink to={{ pathname: `/candidats/missions/${p.row.uuid}/list`, search: `?quantity=${p.value}&isSelected=true` }}
          style={{ textDecoration: 'none' }}>
          <Chip label={p.value ?? 0} size="small" sx={{ background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer', border: '1px solid #bbf7d0' }} />
        </RouterLink>
      ),
    },
    {
      field: 'status', headerName: 'STATUS', width: 110,
      renderCell: (p) => {
        const key = p.value?.toLowerCase();
        const cfg = statusConfig[key] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
        return (
          <Chip label={p.value || 'N/A'} size="small"
            sx={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: '1rem', textTransform: 'uppercase', height: 22 }} />
        );
      },
    },
    {
      field: 'priorite', headerName: 'PRIORITÉ', width: 100,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'localisation', headerName: 'LOCALISATION', width: 140,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'pourvoir', headerName: 'À POURVOIR', width: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'createdAt', headerName: 'DATE DÉBUT', width: 120,
      renderCell: (p) => {
        const d = moment(p.value?.date);
        return <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>{d.isValid() ? d.format('DD/MM/YYYY') : '—'}</Typography>;
      },
    },
    {
      field: 'contact', headerName: 'CONTACT', width: 160,
      renderCell: (p) => (
        <RouterLink to={`/societe/${p.row.societeId}/contacts/${p.row.contactId}/view`}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </RouterLink>
      ),
    },
    {
      field: 'sourceur', headerName: 'SOURCEUR', width: 140,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'consultant', headerName: 'TALENT MANAGER', width: 160,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'accountManager', headerName: 'DELIVERY MANAGER', width: 170,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'actions', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Modifier">
            <IconButton size="small"
              onClick={() => editMission(p.row.uuid, p.row.societeId)}
              sx={{ color: '#eab308', background: '#fefce8', borderRadius: '8px', p: 0.75, '&:hover': { background: '#fef9c3' } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {getUserData()?.role?.includes('ROLE_DIRECTION') && (
            <Tooltip title="Supprimer">
              <IconButton size="small"
                onClick={() => { setMissionToDelete(p.row.uuid); setOpenDialogDelete(true); }}
                sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', p: 0.75, '&:hover': { background: '#fee2e2' } }}>
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={gridContainerSx}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid}
          loading={missionData?.isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog}
        open={openDialogDelete}
        loading={loadToDelete}
        title="Supprimer la mission"
        handleConfirm={() => handleDeleteMission(missionToDelete)}
        label="Voulez-vous vraiment supprimer cette mission ?"
      />
    </>
  );
};

export default MissionsTable;


